import React, { useCallback, useEffect, useState } from 'react';
import {
  CheckCircle2,
  Database,
  Eye,
  EyeOff,
  Loader2,
  Mail,
  RefreshCw,
  Save,
  Settings as SettingsIcon,
} from 'lucide-react';
import { getSystemSettings, updateSystemSettings } from '../services/api';
import { SystemSettings } from '../types';
import AccountSecurityCard from './AccountSecurityCard';
import InvitationManager from './InvitationManager';

interface SettingsProps {
  isAdmin: boolean;
}

type ToggleSetting =
  | 'registration_enabled'
  | 'show_default_login_info'
  | 'login_captcha_enabled'
  | 'item_sync_enabled';

const inputClass =
  'w-full rounded-xl border border-transparent bg-gray-50 px-4 py-3 text-sm text-gray-900 outline-none transition focus:border-yellow-300 focus:bg-white focus:ring-4 focus:ring-yellow-100';

const isSettingEnabled = (value: unknown) =>
  value === true ||
  value === 1 ||
  String(value).trim().toLowerCase() === 'true' ||
  String(value).trim() === '1';

const buildSettingsPayload = (
  settings: SystemSettings,
): Partial<SystemSettings> => ({
  registration_enabled: settings.registration_enabled,
  show_default_login_info: settings.show_default_login_info,
  login_captcha_enabled: settings.login_captcha_enabled,
  item_sync_enabled: settings.item_sync_enabled,
  item_sync_interval: settings.item_sync_interval,
  item_sync_max_pages: settings.item_sync_max_pages,
  smtp_server: settings.smtp_server,
  smtp_port: settings.smtp_port,
  smtp_user: settings.smtp_user,
  smtp_password: settings.smtp_password,
  smtp_from: settings.smtp_from,
});

const Settings: React.FC<SettingsProps> = ({ isAdmin }) => {
  const [settings, setSettings] = useState<SystemSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSmtpPassword, setShowSmtpPassword] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setSettings(await getSystemSettings());
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : '系统配置加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadSettings();
  }, [loadSettings]);

  const handleSave = async () => {
    if (!settings) return;
    setSaving(true);
    setNotice('');
    setError('');
    try {
      await updateSystemSettings(buildSettingsPayload(settings));
      setNotice('系统配置已保存');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : '系统配置保存失败');
    } finally {
      setSaving(false);
    }
  };

  const toggleSetting = (key: ToggleSetting) => {
    if (!settings) return;
    setSettings({
      ...settings,
      [key]: !isSettingEnabled(settings[key]),
    });
  };

  const renderToggle = (
    key: ToggleSetting,
    title: string,
    description: string,
  ) => {
    if (!settings) return null;
    const enabled = isSettingEnabled(settings[key]);
    return (
      <div className="flex items-center justify-between gap-4 rounded-2xl bg-gray-50 p-4">
        <div>
          <div className="font-bold text-gray-900">{title}</div>
          <div className="mt-1 text-xs leading-5 text-gray-500">
            {description}
          </div>
        </div>
        <button
          aria-label={`${enabled ? '关闭' : '开启'}${title}`}
          aria-pressed={enabled}
          className={`relative h-8 w-14 shrink-0 rounded-full transition-all ${
            enabled ? 'bg-[#FFE815]' : 'bg-gray-300'
          }`}
          type="button"
          onClick={() => toggleSetting(key)}
        >
          <span
            className={`absolute top-1 h-6 w-6 rounded-full bg-white shadow-md transition-all ${
              enabled ? 'left-7' : 'left-1'
            }`}
          />
        </button>
      </div>
    );
  };

  const saveButton = (compact = false) => (
    <button
      aria-label="保存系统配置"
      className={`ios-btn-primary inline-flex items-center justify-center gap-2 rounded-xl font-black shadow-lg shadow-yellow-100 transition disabled:cursor-not-allowed disabled:opacity-60 ${
        compact ? 'px-5 py-3 text-sm' : 'px-7 py-3.5 text-base'
      }`}
      disabled={saving || loading || !settings}
      type="button"
      onClick={handleSave}
    >
      {saving ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <Save className="h-4 w-4" />
      )}
      {saving ? '保存中…' : '保存配置'}
    </button>
  );

  if (loading && !settings) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center text-gray-500">
        <Loader2 className="mr-2 h-6 w-6 animate-spin" />
        加载系统配置中…
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="mx-auto max-w-xl rounded-3xl bg-white p-8 text-center shadow-sm">
        <p className="font-bold text-red-600">{error || '系统配置加载失败'}</p>
        <button
          className="mt-5 rounded-xl bg-gray-900 px-5 py-3 text-sm font-bold text-white"
          type="button"
          onClick={loadSettings}
        >
          重新加载
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1500px] space-y-6 pb-10 animate-fade-in">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gray-100">
            <SettingsIcon className="h-6 w-6 text-gray-600" />
          </div>
          <div>
            <h2 className="text-2xl font-extrabold text-gray-900 sm:text-3xl">
              系统设置
            </h2>
            <p className="mt-1 text-sm font-medium text-gray-500">
              管理账户安全、站点规则与邮件通知
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 self-end sm:self-auto">
          <button
            aria-label="刷新系统配置"
            className="inline-flex items-center gap-2 rounded-xl bg-gray-100 px-4 py-3 text-sm font-bold text-gray-700 transition-colors hover:bg-gray-200 disabled:opacity-60"
            disabled={loading || saving}
            type="button"
            onClick={loadSettings}
          >
            <RefreshCw
              className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`}
            />
            刷新
          </button>
          {saveButton(true)}
        </div>
      </header>

      {(notice || error) && (
        <div
          aria-live="polite"
          className={`flex items-center gap-2 rounded-2xl px-4 py-3 text-sm font-bold ${
            error ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-700'
          }`}
        >
          {!error && <CheckCircle2 className="h-4 w-4" />}
          {error || notice}
        </div>
      )}

      <AccountSecurityCard />

      <div
        data-testid="settings-grid"
        className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:gap-8"
      >
        <section className="overflow-hidden rounded-[2rem] border border-white bg-white shadow-sm">
          <div className="flex items-start gap-3 border-b border-gray-100 px-6 py-5">
            <div className="rounded-xl bg-gray-100 p-2 text-gray-600">
              <Database className="h-4 w-4" />
            </div>
            <div>
              <h3 className="text-lg font-extrabold text-gray-900">基础设置</h3>
              <p className="mt-1 text-xs leading-5 text-gray-500">
                控制新用户入口、登录安全和商品同步任务。
              </p>
            </div>
          </div>
          <div className="space-y-4 p-6">
            {renderToggle(
              'registration_enabled',
              '允许用户注册',
              '开启后，新用户仍需有效邀请码才能完成注册。',
            )}
            {renderToggle(
              'show_default_login_info',
              '显示默认登录信息',
              '仅用于初次部署提示，正式使用建议关闭。',
            )}
            {renderToggle(
              'login_captcha_enabled',
              '登录滑动验证码',
              '账号密码登录前要求完成滑动验证。',
            )}
            {renderToggle(
              'item_sync_enabled',
              '启用商品自动同步',
              '定时获取闲鱼商品信息并更新本地数据。',
            )}

            <div className="grid grid-cols-1 gap-4 pt-1 sm:grid-cols-2">
              <label className="space-y-2 text-sm font-bold text-gray-800">
                <span>商品同步间隔（分钟）</span>
                <input
                  aria-label="商品同步间隔（分钟）"
                  className={inputClass}
                  max={1_440}
                  min={1}
                  type="number"
                  value={Math.round((settings.item_sync_interval ?? 600) / 60)}
                  onChange={(event) => {
                    const minutes = Number.parseInt(event.target.value, 10) || 10;
                    setSettings({
                      ...settings,
                      item_sync_interval: minutes * 60,
                    });
                  }}
                />
                <span className="block text-xs font-normal text-gray-400">
                  建议 10–60 分钟
                </span>
              </label>
              <label className="space-y-2 text-sm font-bold text-gray-800">
                <span>每次最多同步页数</span>
                <input
                  aria-label="每次最多同步页数"
                  className={inputClass}
                  max={50}
                  min={1}
                  type="number"
                  value={settings.item_sync_max_pages ?? 5}
                  onChange={(event) =>
                    setSettings({
                      ...settings,
                      item_sync_max_pages:
                        Number.parseInt(event.target.value, 10) || 5,
                    })
                  }
                />
                <span className="block text-xs font-normal text-gray-400">
                  每页最多 20 个商品
                </span>
              </label>
            </div>
          </div>
        </section>

        <section className="overflow-hidden rounded-[2rem] border border-white bg-white shadow-sm">
          <div className="flex items-start gap-3 border-b border-gray-100 px-6 py-5">
            <div className="rounded-xl bg-blue-50 p-2 text-blue-600">
              <Mail className="h-4 w-4" />
            </div>
            <div>
              <h3 className="text-lg font-extrabold text-gray-900">邮件通知</h3>
              <p className="mt-1 text-xs leading-5 text-gray-500">
                用于发送注册邮箱验证码，建议使用邮箱授权码。
              </p>
            </div>
          </div>
          <div className="space-y-5 p-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <label className="space-y-2 text-sm font-bold text-gray-800">
                <span>SMTP 服务器</span>
                <input
                  aria-label="SMTP 服务器"
                  className={inputClass}
                  placeholder="smtp.qq.com"
                  type="text"
                  value={settings.smtp_server ?? ''}
                  onChange={(event) =>
                    setSettings({
                      ...settings,
                      smtp_server: event.target.value,
                    })
                  }
                />
              </label>
              <label className="space-y-2 text-sm font-bold text-gray-800">
                <span>SMTP 端口</span>
                <input
                  aria-label="SMTP 端口"
                  className={inputClass}
                  placeholder="587"
                  type="number"
                  value={settings.smtp_port ?? 587}
                  onChange={(event) =>
                    setSettings({
                      ...settings,
                      smtp_port: Number.parseInt(event.target.value, 10) || 587,
                    })
                  }
                />
              </label>
            </div>

            <label className="block space-y-2 text-sm font-bold text-gray-800">
              <span>发件邮箱</span>
              <input
                aria-label="发件邮箱"
                className={inputClass}
                placeholder="your-email@qq.com"
                type="email"
                value={settings.smtp_user ?? ''}
                onChange={(event) =>
                  setSettings({ ...settings, smtp_user: event.target.value })
                }
              />
            </label>

            <label className="block space-y-2 text-sm font-bold text-gray-800">
              <span>邮箱密码 / 授权码</span>
              <div className="relative">
                <input
                  aria-label="邮箱密码 / 授权码"
                  autoComplete="new-password"
                  className={`${inputClass} pr-12`}
                  placeholder={
                    settings.smtp_password_configured
                      ? '已配置，留空则保持不变'
                      : '输入密码或授权码'
                  }
                  type={showSmtpPassword ? 'text' : 'password'}
                  value={settings.smtp_password ?? ''}
                  onChange={(event) =>
                    setSettings({
                      ...settings,
                      smtp_password: event.target.value,
                    })
                  }
                />
                <button
                  aria-label={
                    showSmtpPassword ? '隐藏邮箱授权码' : '显示邮箱授权码'
                  }
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-2 text-gray-400 transition hover:bg-white hover:text-gray-700"
                  type="button"
                  onClick={() => setShowSmtpPassword((previous) => !previous)}
                >
                  {showSmtpPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              <span className="block text-xs font-normal text-gray-400">
                {settings.smtp_password_configured
                  ? `授权码已配置${
                      settings.smtp_password_suffix
                        ? `，尾号 ${settings.smtp_password_suffix}`
                        : ''
                    }`
                  : 'QQ 邮箱等服务通常需要使用授权码'}
              </span>
            </label>

            <label className="block space-y-2 text-sm font-bold text-gray-800">
              <span>发件人显示名（可选）</span>
              <input
                aria-label="发件人显示名"
                className={inputClass}
                placeholder="闲鱼智控"
                type="text"
                value={settings.smtp_from ?? ''}
                onChange={(event) =>
                  setSettings({ ...settings, smtp_from: event.target.value })
                }
              />
            </label>

            <div className="rounded-2xl bg-blue-50 px-4 py-3 text-xs leading-5 text-blue-700">
              AI 模型、Key 和客服策略已统一迁移到左侧“AI 接待”，这里不再重复配置。
            </div>
          </div>
        </section>
      </div>

      {isAdmin && <InvitationManager />}

      <section className="flex flex-col gap-4 rounded-[2rem] border border-yellow-100 bg-gradient-to-r from-yellow-50 via-white to-white p-6 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="font-black text-gray-900">保存系统配置</h3>
          <p className="mt-1 text-sm text-gray-500">
            账户密码由上方安全卡片独立修改，不受此按钮影响。
          </p>
        </div>
        {saveButton()}
      </section>
    </div>
  );
};

export default Settings;
